import React, { useState, useCallback, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import type { RootState } from "../../store";
import { useMsal } from "@azure/msal-react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../../components/Sidebar";
import {
  Box,
  Tabs,
  Tab,
  Switch,
  Alert,
  Paper,
  CircularProgress,
  Collapse,
  IconButton,
  Button,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  TableSortLabel,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  PowerSettingsNew as PowerIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Save as SaveIcon,
} from "@mui/icons-material";
import axiosClient from "../../api/AxiosClient";
import type { Agents } from "../../types/master";
import "../Dashboard.scss";
import styles from "./admin.module.scss";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`admin-tabpanel-${index}`}
      aria-labelledby={`admin-tab-${index}`}
      {...other}
    >
      {value === index && <Box className={styles.tabContent}>{children}</Box>}
    </div>
  );
}

interface CategoryAgents {
  [category: string]: Agents[];
}

interface InferenceLog {
  id: number;
  prompt_question: string;
  prompt_response: string;
  user_email_id: string;
  created_date: string;
  agent_id: number;
  parent_agent: string;
  prompt_tokens: number;
  completion_tokens: number;
  total_tokens: number;
  model_type: string | null;
  workflow_id: string;
  response_type: string;
}

interface InferenceLogsResponse {
  total: number;
  limit: number;
  offset: number;
  data: InferenceLog[];
}

interface Role {
  id: number;
  name: string;
}

interface UserAccess {
  userId: number;
  userName: string | null;
  userEmail: string;
  is_admin: number;
}

interface UserAccessPagination {
  total: number;
  limit: number;
  offset: number;
  hasMore?: boolean;
}

// API Response interfaces (nested structure)
interface UserData {
  id: number;
  name: string | null;
  email: string;
  is_admin?: number; // This field may or may not be in the response
}

interface UserAccessAPIItem {
  user: UserData;
  agents: Array<{
    id: number;
    name: string;
    category: string;
  }>;
}

interface UserAccessAPIResponse {
  data: UserAccessAPIItem[];
  pagination: UserAccessPagination;
}

export default function AdminPage(): JSX.Element {
  const user = useSelector((state: RootState) => state.auth.user);
  const { instance } = useMsal();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState(0);
  const [agents, setAgents] = useState<Agents[]>([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set(),
  );
  const [pendingChanges, setPendingChanges] = useState<Map<number, number>>(
    new Map(),
  );
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });

  // Roles state
  const roles: Role[] = useMemo(
    () => [
      { id: 1001, name: "CXO" },
      { id: 1002, name: "COS" },
    ],
    [],
  );

  // Logs state
  const [logs, setLogs] = useState<InferenceLog[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [totalLogs, setTotalLogs] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [selectedLog, setSelectedLog] = useState<InferenceLog | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  // Admin Controls state
  const [users, setUsers] = useState<UserAccess[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [totalUsers, setTotalUsers] = useState(0);
  const [usersPage, setUsersPage] = useState(0);
  const [usersRowsPerPage, setUsersRowsPerPage] = useState(50);
  const [usersSortBy, setUsersSortBy] = useState("userName");
  const [usersSortOrder, setUsersSortOrder] = useState<"asc" | "desc">("asc");
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [pendingUserChanges, setPendingUserChanges] = useState<
    Map<number, number>
  >(new Map());
  const [usersSaving, setUsersSaving] = useState(false);

  const handleTabChange = useCallback(
    (_event: React.SyntheticEvent, newValue: number) => {
      setActiveTab(newValue);
    },
    [],
  );

  const handleLogout = useCallback(() => {
    instance.logoutRedirect({
      postLogoutRedirectUri: "/",
    });
  }, [instance]);

  const handleNavClick = useCallback(
    (navId: string) => {
      switch (navId) {
        case "home":
          navigate("/landing");
          break;
        case "analytics":
          navigate("/onboarding");
          break;
        case "search":
          navigate("/performance");
          break;
        case "token":
          navigate("/tokenconsumption");
          break;
        case "feedback":
          navigate("/feedback");
          break;
        case "user-management":
          navigate("/user-management");
          break;
        default:
          break;
      }
    },
    [navigate],
  );

  const fetchAgents = async () => {
    try {
      setLoading(true);
      const response = await axiosClient.get<Agents[]>(
        "/agents/?all_agents=true",
      );
      setAgents(response.data);
    } catch (err) {
      console.error("Failed to fetch agents:", err);
      setSnackbar({
        open: true,
        message: "Failed to load agents",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchLogs = useCallback(async () => {
    try {
      setLogsLoading(true);
      const offset = page * rowsPerPage;
      const response = await axiosClient.get<InferenceLogsResponse>(
        `/inference_logs?limit=${rowsPerPage}&offset=${offset}`,
      );
      setLogs(response.data.data);
      setTotalLogs(response.data.total);
    } catch (err) {
      console.error("Failed to fetch logs:", err);
      setSnackbar({
        open: true,
        message: "Failed to load logs",
        severity: "error",
      });
    } finally {
      setLogsLoading(false);
    }
  }, [page, rowsPerPage]);

  useEffect(() => {
    fetchAgents();
  }, []);

  useEffect(() => {
    if (activeTab === 0) {
      fetchLogs();
    }
  }, [activeTab, fetchLogs]);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch users for Admin Controls
  const fetchUsers = useCallback(async () => {
    try {
      setUsersLoading(true);
      const offset = usersPage * usersRowsPerPage;

      // Query params for pagination and sorting
      const queryParams = new URLSearchParams({
        limit: usersRowsPerPage.toString(),
        offset: offset.toString(),
        sortBy: usersSortBy,
        sortOrder: usersSortOrder,
      });

      // Request body for search criteria
      const requestBody = {
        search_by_user_name: debouncedSearch.trim(),
        search_by_email: debouncedSearch.trim(),
        search_by_agent_id: [],
        search_by_agent_category: [],
      };

      const response = await axiosClient.post<UserAccessAPIResponse>(
        `/admin/user-agent-access?${queryParams.toString()}`,
        requestBody,
      );

      // Map nested API response to flat UserAccess structure
      const mappedUsers: UserAccess[] = (response.data.data || []).map(
        (item) => ({
          userId: item.user.id,
          userName: item.user.name,
          userEmail: item.user.email,
          is_admin: item.user.is_admin ?? 0, // Default to 0 if not present
        }),
      );

      setUsers(mappedUsers);
      setTotalUsers(response.data.pagination?.total || 0);
    } catch (err) {
      console.error("Failed to fetch users:", err);
      setSnackbar({
        open: true,
        message: "Failed to load users",
        severity: "error",
      });
    } finally {
      setUsersLoading(false);
    }
  }, [
    usersPage,
    usersRowsPerPage,
    usersSortBy,
    usersSortOrder,
    debouncedSearch,
  ]);

  // Fetch users when tab becomes active or dependencies change
  useEffect(() => {
    if (activeTab === 4) {
      fetchUsers();
    }
  }, [activeTab, fetchUsers]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleViewLog = (log: InferenceLog) => {
    setSelectedLog(log);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedLog(null);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString("en-US", {
      month: "numeric",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });
  };

  const getStatusColor = (totalTokens: number) => {
    if (totalTokens > 0) return "success";
    return "warning";
  };

  const handleRefreshLogs = () => {
    fetchLogs();
  };

  const categorizedAgents = useMemo(() => {
    const grouped: CategoryAgents = {};
    agents.forEach((agent) => {
      const category = agent.category || "Uncategorized";
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push(agent);
    });
    Object.keys(grouped).forEach((category) => {
      grouped[category].sort((a, b) =>
        a.agent_name.localeCompare(b.agent_name),
      );
    });
    return grouped;
  }, [agents]);

  const categoryStats = useMemo(() => {
    const stats: Record<
      string,
      { total: number; active: number; disabled: number }
    > = {};
    Object.entries(categorizedAgents).forEach(([category, agentList]) => {
      const active = agentList.filter((a) => {
        const status = pendingChanges.has(a.id)
          ? pendingChanges.get(a.id)!
          : a.status;
        return status === 1;
      }).length;
      stats[category] = {
        total: agentList.length,
        active,
        disabled: agentList.length - active,
      };
    });
    return stats;
  }, [categorizedAgents, pendingChanges]);

  const totalStats = useMemo(() => {
    const total = agents.length;
    const disabled = agents.filter((a) => {
      const status = pendingChanges.has(a.id)
        ? pendingChanges.get(a.id)!
        : a.status;
      return status === 0;
    }).length;
    return { total, disabled, active: total - disabled };
  }, [agents, pendingChanges]);

  const toggleCategory = (category: string) => {
    setExpandedCategories((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(category)) {
        newSet.delete(category);
      } else {
        newSet.add(category);
      }
      return newSet;
    });
  };

  const toggleAgent = (agentId: number, currentStatus: number) => {
    const newStatus = currentStatus === 1 ? 0 : 1;
    setPendingChanges((prev) => {
      const newMap = new Map(prev);
      newMap.set(agentId, newStatus);
      return newMap;
    });
  };

  const toggleCategoryAgents = (category: string, newStatus: number) => {
    setPendingChanges((prev) => {
      const newMap = new Map(prev);
      categorizedAgents[category].forEach((agent) => {
        newMap.set(agent.id, newStatus);
      });
      return newMap;
    });
  };

  const applyChanges = async () => {
    if (pendingChanges.size === 0) return;

    try {
      setUpdating(true);
      const updates = Array.from(pendingChanges.entries()).map(
        ([agent_id, status]) => ({ agent_id, is_display: status }),
      );

      await axiosClient.put("/agents/status", { updates });

      setPendingChanges(new Map());

      await fetchAgents();

      setSnackbar({
        open: true,
        message: `Successfully updated ${updates.length} agent${updates.length > 1 ? "s" : ""}`,
        severity: "success",
      });
    } catch (err) {
      console.error("Failed to update agents:", err);
      setSnackbar({
        open: true,
        message: "Failed to update kill switches",
        severity: "error",
      });
    } finally {
      setUpdating(false);
    }
  };

  const discardChanges = () => {
    setPendingChanges(new Map());
  };

  // Admin Controls handlers
  const handleUsersPageChange = (_event: unknown, newPage: number) => {
    setUsersPage(newPage);
  };

  const handleUsersRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setUsersRowsPerPage(parseInt(event.target.value, 10));
    setUsersPage(0);
  };

  const handleUsersSortChange = (column: string) => {
    if (usersSortBy === column) {
      // Toggle sort order
      setUsersSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setUsersSortBy(column);
      setUsersSortOrder("asc");
    }
  };

  const toggleUserAdmin = (userId: number, currentStatus: number) => {
    const newStatus = currentStatus === 1 ? 0 : 1;
    setPendingUserChanges((prev) => {
      const newMap = new Map(prev);
      newMap.set(userId, newStatus);
      return newMap;
    });
  };

  const saveUserChanges = async () => {
    if (pendingUserChanges.size === 0) return;

    try {
      setUsersSaving(true);
      const updates = Array.from(pendingUserChanges.entries()).map(
        ([userId, is_admin]) => ({ userId, is_admin }),
      );

      await axiosClient.put("/admin/users/is_admin_bulk", { users: updates });

      setPendingUserChanges(new Map());
      await fetchUsers();

      setSnackbar({
        open: true,
        message: `Successfully updated ${updates.length} user${updates.length > 1 ? "s" : ""}`,
        severity: "success",
      });
    } catch (err: any) {
      console.error("Failed to update users:", err);
      setSnackbar({
        open: true,
        message: err.response?.data?.message || "Failed to update admin access",
        severity: "error",
      });
    } finally {
      setUsersSaving(false);
    }
  };

  const discardUserChanges = () => {
    setPendingUserChanges(new Map());
  };

  const clearSearch = () => {
    setSearchQuery("");
  };

  return (
    <Box className="dashboard-root">
      <Sidebar
        user={user || undefined}
        onLogout={handleLogout}
        activeNav="admin"
        onNavClick={handleNavClick}
      />
      <Box className="main-area">
        <div className="header">
          <div className="header-left">
            <h1 className="page-title">Admin Panel</h1>
          </div>
        </div>

        <div className="scrollContent">
          <Paper elevation={0} className={styles.tabsContainer}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              aria-label="Admin tabs"
              className={styles.tabs}
            >
              <Tab label="Roles" />
              <Tab label="Links" />
              <Tab label="Logs" />
              <Tab label="Kill Switch" />
              <Tab label="Admin Controls" />
            </Tabs>
          </Paper>

          <TabPanel value={activeTab} index={0}>
            <div className={styles.logsContainer}>
              <div className={styles.logsHeader}>
                <div className={styles.logsInfo}>
                  <p className="subtle">Showing {roles.length} roles</p>
                </div>
              </div>

              <TableContainer
                component={Paper}
                elevation={0}
                className={styles.tableContainer}
              >
                <Table>
                  <TableHead>
                    <TableRow className={styles.tableHeader}>
                      <TableCell>ID</TableCell>
                      <TableCell>Name</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {roles.map((role) => (
                      <TableRow key={role.id} className={styles.tableRow}>
                        <TableCell>{role.id}</TableCell>
                        <TableCell>{role.name}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </div>
          </TabPanel>

          <TabPanel value={activeTab} index={1}>
            <div className={styles.logsContainer}>
              <div className={styles.logsHeader}>
                <div className={styles.logsInfo}>
                  <p className="subtle">Links data will be added later</p>
                </div>
              </div>
            </div>
          </TabPanel>

          <TabPanel value={activeTab} index={2}>
            <div className={styles.logsContainer}>
              <div className={styles.logsHeader}>
                <div className={styles.logsInfo}>
                  <p className="subtle">
                    Showing {logs.length} of {totalLogs.toLocaleString()}{" "}
                    inference logs
                  </p>
                </div>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={handleRefreshLogs}
                  disabled={logsLoading}
                  className={styles.refreshButton}
                >
                  Refresh
                </Button>
              </div>

              {logsLoading ? (
                <div className={styles.loadingContainer}>
                  <CircularProgress sx={{ color: "var(--accent)" }} />
                  <p className="subtle">Loading inference logs...</p>
                </div>
              ) : (
                <>
                  <TableContainer
                    component={Paper}
                    elevation={0}
                    className={styles.tableContainer}
                  >
                    <Table>
                      <TableHead>
                        <TableRow className={styles.tableHeader}>
                          <TableCell>Timestamp</TableCell>
                          <TableCell>User</TableCell>
                          <TableCell>Agent</TableCell>
                          <TableCell>Response Type</TableCell>
                          <TableCell>Question</TableCell>
                          <TableCell align="center">Tokens</TableCell>
                          <TableCell>Model</TableCell>
                          <TableCell align="center">Status</TableCell>
                          <TableCell align="center">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {logs.map((log) => (
                          <TableRow key={log.id} className={styles.tableRow}>
                            <TableCell className={styles.timestampCell}>
                              {formatDate(log.created_date)}
                            </TableCell>
                            <TableCell>{log.user_email_id || "—"}</TableCell>
                            <TableCell>
                              <Chip
                                label={log.parent_agent || "Unknown"}
                                size="small"
                                className={styles.agentChip}
                              />
                            </TableCell>
                            <TableCell>
                              <span className={styles.responseType}>
                                {log.response_type || "—"}
                              </span>
                            </TableCell>
                            <TableCell>
                              <div className={styles.questionCell}>
                                {log.prompt_question.length > 60
                                  ? `${log.prompt_question.substring(0, 60)}...`
                                  : log.prompt_question}
                              </div>
                            </TableCell>
                            <TableCell align="center">
                              {log.total_tokens > 0 ? (
                                <Chip
                                  label={log.total_tokens.toLocaleString()}
                                  size="small"
                                  className={styles.tokenChip}
                                />
                              ) : (
                                "—"
                              )}
                            </TableCell>
                            <TableCell>
                              {log.model_type ? (
                                <span className={styles.modelBadge}>
                                  {log.model_type}
                                </span>
                              ) : (
                                "—"
                              )}
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={
                                  log.total_tokens > 0 ? "success" : "pending"
                                }
                                size="small"
                                className={
                                  log.total_tokens > 0
                                    ? styles.statusSuccess
                                    : styles.statusPending
                                }
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Button
                                size="small"
                                variant="contained"
                                onClick={() => handleViewLog(log)}
                                className={styles.viewButton}
                              >
                                View
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <div className={styles.paginationContainer}>
                    <TablePagination
                      component="div"
                      count={totalLogs}
                      page={page}
                      onPageChange={handleChangePage}
                      rowsPerPage={rowsPerPage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                      rowsPerPageOptions={[10, 20, 50, 100]}
                      className={styles.pagination}
                    />
                  </div>
                </>
              )}
            </div>

            {/* Log Details Dialog */}
            <Dialog
              open={dialogOpen}
              onClose={handleCloseDialog}
              maxWidth="md"
              fullWidth
            >
              <DialogTitle>Log Details</DialogTitle>
              <DialogContent dividers>
                {selectedLog && (
                  <div className={styles.logDetails}>
                    <div className={styles.detailRow}>
                      <strong>Timestamp:</strong>
                      <span>{formatDate(selectedLog.created_date)}</span>
                    </div>
                    <div className={styles.detailRow}>
                      <strong>User Email:</strong>
                      <span>{selectedLog.user_email_id}</span>
                    </div>
                    <div className={styles.detailRow}>
                      <strong>Agent:</strong>
                      <span>{selectedLog.parent_agent}</span>
                    </div>
                    <div className={styles.detailRow}>
                      <strong>Agent ID:</strong>
                      <span>{selectedLog.agent_id}</span>
                    </div>
                    <div className={styles.detailRow}>
                      <strong>Workflow ID:</strong>
                      <span className={styles.workflowId}>
                        {selectedLog.workflow_id}
                      </span>
                    </div>
                    <div className={styles.detailRow}>
                      <strong>Model:</strong>
                      <span>{selectedLog.model_type || "—"}</span>
                    </div>
                    <div className={styles.detailRow}>
                      <strong>Tokens:</strong>
                      <span>
                        {selectedLog.total_tokens > 0
                          ? `${selectedLog.total_tokens} (Prompt: ${selectedLog.prompt_tokens}, Completion: ${selectedLog.completion_tokens})`
                          : "—"}
                      </span>
                    </div>
                    <div className={styles.detailSection}>
                      <strong>Question:</strong>
                      <pre className={styles.jsonContent}>
                        {selectedLog.prompt_question}
                      </pre>
                    </div>
                    <div className={styles.detailSection}>
                      <strong>Response:</strong>
                      <pre className={styles.jsonContent}>
                        {JSON.stringify(
                          JSON.parse(selectedLog.prompt_response),
                          null,
                          2,
                        )}
                      </pre>
                    </div>
                  </div>
                )}
              </DialogContent>
              <DialogActions>
                <Button onClick={handleCloseDialog} variant="outlined">
                  Close
                </Button>
              </DialogActions>
            </Dialog>
          </TabPanel>

          <TabPanel value={activeTab} index={3}>
            <div className={styles.killSwitchContainer}>
              <Alert
                severity={totalStats.disabled > 0 ? "warning" : "success"}
                className={styles.statusAlert}
              >
                <strong>
                  {totalStats.active} of {totalStats.total} agents active
                </strong>
                {totalStats.disabled > 0 &&
                  ` — ${totalStats.disabled} agent${totalStats.disabled > 1 ? "s" : ""} disabled`}
              </Alert>

              {loading ? (
                <div className={styles.loadingContainer}>
                  <CircularProgress size={40} sx={{ color: "var(--accent)" }} />
                  <p className="subtle">Loading agents...</p>
                </div>
              ) : (
                <>
                  {pendingChanges.size > 0 && (
                    <div className={styles.actionBar}>
                      <p className={styles.changesInfo}>
                        {pendingChanges.size} pending change
                        {pendingChanges.size > 1 ? "s" : ""}
                      </p>
                      <div className={styles.actionButtons}>
                        <Button
                          variant="outlined"
                          onClick={discardChanges}
                          disabled={updating}
                          className={styles.discardButton}
                        >
                          Discard
                        </Button>
                        <Button
                          variant="contained"
                          onClick={applyChanges}
                          disabled={updating}
                          className={styles.applyButton}
                        >
                          {updating ? "Applying..." : "Apply Changes"}
                        </Button>
                      </div>
                    </div>
                  )}

                  <div className={styles.categoriesList}>
                    {Object.entries(categorizedAgents)
                      .sort(([a], [b]) => a.localeCompare(b))
                      .map(([category, agentList]) => {
                        const stats = categoryStats[category];
                        const isExpanded = expandedCategories.has(category);
                        const allActive = agentList.every((a) => {
                          const status = pendingChanges.has(a.id)
                            ? pendingChanges.get(a.id)!
                            : a.status;
                          return status === 1;
                        });

                        return (
                          <div key={category} className={styles.categoryCard}>
                            <div className={styles.categoryHeader}>
                              <div className={styles.categoryInfo}>
                                <h3>{category}</h3>
                                <p className="subtle">
                                  {stats.active} active, {stats.disabled}{" "}
                                  disabled
                                </p>
                              </div>
                              <div className={styles.categoryActions}>
                                <div className={styles.categoryToggle}>
                                  <span className="subtle">
                                    {allActive ? "Disable All" : "Enable All"}
                                  </span>
                                  <Switch
                                    checked={allActive}
                                    onChange={(e) =>
                                      toggleCategoryAgents(
                                        category,
                                        e.target.checked ? 1 : 0,
                                      )
                                    }
                                    sx={{
                                      "& .MuiSwitch-switchBase.Mui-checked": {
                                        color: "var(--success)",
                                      },
                                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                                        {
                                          backgroundColor: "var(--success)",
                                        },
                                    }}
                                  />
                                </div>
                                <IconButton
                                  onClick={() => toggleCategory(category)}
                                  size="small"
                                >
                                  {isExpanded ? (
                                    <ExpandLessIcon />
                                  ) : (
                                    <ExpandMoreIcon />
                                  )}
                                </IconButton>
                              </div>
                            </div>

                            <Collapse in={isExpanded}>
                              <div className={styles.agentsList}>
                                {agentList.map((agent) => {
                                  const currentStatus = pendingChanges.has(
                                    agent.id,
                                  )
                                    ? pendingChanges.get(agent.id)!
                                    : agent.status;
                                  const isActive = currentStatus === 1;
                                  const hasChanged = pendingChanges.has(
                                    agent.id,
                                  );

                                  return (
                                    <div
                                      key={agent.id}
                                      className={`${styles.agentItem} ${hasChanged ? styles.changed : ""}`}
                                    >
                                      <div className={styles.agentInfo}>
                                        <div
                                          className={`${styles.statusIndicator} ${isActive ? styles.active : styles.inactive}`}
                                        >
                                          <PowerIcon fontSize="small" />
                                        </div>
                                        <div>
                                          <h4>{agent.agent_name}</h4>
                                          <p className="subtle">
                                            {agent.short_description}
                                          </p>
                                        </div>
                                      </div>
                                      <Switch
                                        checked={isActive}
                                        onChange={() =>
                                          toggleAgent(agent.id, currentStatus)
                                        }
                                        sx={{
                                          "& .MuiSwitch-switchBase.Mui-checked":
                                            {
                                              color: "var(--success)",
                                            },
                                          "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                                            {
                                              backgroundColor: "var(--success)",
                                            },
                                        }}
                                      />
                                    </div>
                                  );
                                })}
                              </div>
                            </Collapse>
                          </div>
                        );
                      })}
                  </div>
                </>
              )}
            </div>
          </TabPanel>

          <TabPanel value={activeTab} index={4}>
            <div className={styles.logsContainer}>
              <div className={styles.logsHeader}>
                <div className={styles.logsInfo}>
                  <p className="subtle">
                    Showing {users.length} of {totalUsers.toLocaleString()}{" "}
                    users
                  </p>
                </div>
                <div className={styles.headerActions}>
                  <TextField
                    size="small"
                    placeholder="Search by User ID or Email"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                      endAdornment: searchQuery && (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={clearSearch}>
                            <ClearIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{ minWidth: 300, mr: 2 }}
                  />
                  <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={fetchUsers}
                    disabled={usersLoading}
                    className={styles.refreshButton}
                  >
                    Refresh
                  </Button>
                </div>
              </div>

              {pendingUserChanges.size > 0 && (
                <Alert
                  severity="info"
                  className={styles.actionBar}
                  action={
                    <div className={styles.actionButtons}>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={discardUserChanges}
                        disabled={usersSaving}
                      >
                        Discard
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<SaveIcon />}
                        onClick={saveUserChanges}
                        disabled={usersSaving}
                        className={styles.applyButton}
                      >
                        {usersSaving ? "Saving..." : "Save Changes"}
                      </Button>
                    </div>
                  }
                >
                  <strong>
                    {pendingUserChanges.size} pending change
                    {pendingUserChanges.size > 1 ? "s" : ""}
                  </strong>
                </Alert>
              )}

              {usersLoading ? (
                <div className={styles.loadingContainer}>
                  <CircularProgress sx={{ color: "var(--accent)" }} />
                  <p className="subtle">Loading users...</p>
                </div>
              ) : (
                <>
                  <TableContainer
                    component={Paper}
                    elevation={0}
                    className={styles.tableContainer}
                  >
                    <Table>
                      <TableHead>
                        <TableRow className={styles.tableHeader}>
                          <TableCell>
                            <TableSortLabel
                              active={usersSortBy === "userId"}
                              direction={
                                usersSortBy === "userId"
                                  ? usersSortOrder
                                  : "asc"
                              }
                              onClick={() => handleUsersSortChange("userId")}
                            >
                              User ID
                            </TableSortLabel>
                          </TableCell>
                          <TableCell>
                            <TableSortLabel
                              active={usersSortBy === "userName"}
                              direction={
                                usersSortBy === "userName"
                                  ? usersSortOrder
                                  : "asc"
                              }
                              onClick={() => handleUsersSortChange("userName")}
                            >
                              Name
                            </TableSortLabel>
                          </TableCell>
                          <TableCell>
                            <TableSortLabel
                              active={usersSortBy === "userEmail"}
                              direction={
                                usersSortBy === "userEmail"
                                  ? usersSortOrder
                                  : "asc"
                              }
                              onClick={() => handleUsersSortChange("userEmail")}
                            >
                              Email
                            </TableSortLabel>
                          </TableCell>
                          <TableCell align="center">
                            <TableSortLabel
                              active={usersSortBy === "is_admin"}
                              direction={
                                usersSortBy === "is_admin"
                                  ? usersSortOrder
                                  : "asc"
                              }
                              onClick={() => handleUsersSortChange("is_admin")}
                            >
                              Admin Access
                            </TableSortLabel>
                          </TableCell>
                          <TableCell align="center">Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {users.map((user) => {
                          const currentStatus = pendingUserChanges.has(
                            user.userId,
                          )
                            ? pendingUserChanges.get(user.userId)!
                            : user.is_admin;
                          const isAdmin = currentStatus === 1;
                          const hasChanged = pendingUserChanges.has(
                            user.userId,
                          );

                          return (
                            <TableRow
                              key={user.userId}
                              className={`${styles.tableRow} ${hasChanged ? styles.changed : ""}`}
                            >
                              <TableCell>{user.userId}</TableCell>
                              <TableCell>{user.userName || "—"}</TableCell>
                              <TableCell>{user.userEmail}</TableCell>
                              <TableCell align="center">
                                <Switch
                                  checked={isAdmin}
                                  onChange={() =>
                                    toggleUserAdmin(user.userId, currentStatus)
                                  }
                                  sx={{
                                    "& .MuiSwitch-switchBase.Mui-checked": {
                                      color: "var(--accent)",
                                    },
                                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                                      {
                                        backgroundColor: "var(--accent)",
                                      },
                                  }}
                                />
                              </TableCell>
                              <TableCell align="center">
                                <Chip
                                  label={isAdmin ? "Admin" : "User"}
                                  size="small"
                                  color={isAdmin ? "success" : "default"}
                                  className={
                                    isAdmin
                                      ? styles.statusSuccess
                                      : styles.statusDefault
                                  }
                                />
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <div className={styles.paginationContainer}>
                    <TablePagination
                      component="div"
                      count={totalUsers}
                      page={usersPage}
                      onPageChange={handleUsersPageChange}
                      rowsPerPage={usersRowsPerPage}
                      onRowsPerPageChange={handleUsersRowsPerPageChange}
                      rowsPerPageOptions={[10, 25, 50, 100]}
                      className={styles.pagination}
                    />
                  </div>
                </>
              )}
            </div>
          </TabPanel>
        </div>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
